import React from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { eventColors } from '../../../utils/consts/index';
import { hasPendingSubRequest } from '../../../utils/lessonInstructorUtils';
import { CustomDayCellWrapper } from './CustomComponents/DayCellWrapper';
import { EventCell } from './CustomComponents/EventCell';
import { EventWrapper } from './CustomComponents/EventWrapper';
import Toolbar from './CustomComponents/Toolbar';
// Define your locales if necessary
import { format, getDay, parse, startOfWeek } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { Lesson } from '../../../types/lesson.type';
import { dateInTimeZone } from '../../../utils/dateUtils';

const locales: { [key: string]: any } = {
  'en-US': enUS
};

// Localizer configuration
const localizer = dateFnsLocalizer({
  format: (date: Date, formatString: string, culture?: string) => {
    return format(date, formatString, { locale: locales[culture || 'en-US'] });
  },
  parse: (dateString: string, formatString: string, culture?: string) =>
    parse(dateString, formatString, new Date(), { locale: locales[culture || 'en-US'] }),
  startOfWeek: (culture?: string) =>
    startOfWeek(new Date(), { locale: locales[culture || 'en-US'] }),
  getDay: (date: Date) => getDay(date),
  locales
});

export interface Event {
  title: string;
  start: Date;
  end: Date;
  type: string;
  lesson?: Lesson;
}

export const eventStyleGetter = (event: Event) => {
  const style = {
    backgroundColor: eventColors[event.type],
    borderRadius: '4px',
    color: 'white',
    marginBottom: 2
  };

  return {
    style
  };
};

export const createEventStyleGetterWithInstructor = (filteredInstructorId?: number | null) => {
  return (event: Event) => {
    let backgroundColor = eventColors[event.type];

    if (filteredInstructorId && event.lesson?.lesson_assignments) {
      const assignments = event.lesson.lesson_assignments;
      const instructorId = Number(filteredInstructorId);
      const isMainForSession = event.lesson.course?.course_assignments?.some(
        (ca) => Number(ca.instructor_id) === instructorId
      );

      if (!assignments || assignments.length === 0) {
        backgroundColor = eventColors.mainInstructor;
      } else {
        const instructorAssignment = assignments.find(
          (assignment) => Number(assignment.instructor_id) === instructorId
        );

        if (instructorAssignment) {
          const role =
            instructorAssignment.instructor_role ??
            (isMainForSession ? 'instructor' : 'substitute');

          switch (role) {
            case 'substitute':
              backgroundColor = eventColors.substitute;
              break;
            case 'instructor':
              backgroundColor = eventColors.addInstructor;
              break;
            case 'trainee':
              backgroundColor = eventColors.trainee;
              break;
            case 'observer':
              backgroundColor = eventColors.observer;
              break;
            default:
              backgroundColor = isMainForSession
                ? eventColors.mainInstructor
                : eventColors.substitute;
          }
        } else if (isMainForSession) {
          backgroundColor = eventColors.mainInstructor;
        }
      }
    }

    const pendingSubRequest = Boolean(
      filteredInstructorId &&
        event.lesson?.lesson_assignments &&
        hasPendingSubRequest(event.lesson.lesson_assignments, filteredInstructorId)
    );

    if (pendingSubRequest) {
      return {
        style: {
          backgroundColor: `${backgroundColor}18`,
          border: `2px dashed ${backgroundColor}`,
          borderRadius: '4px',
          color: backgroundColor,
          marginBottom: 2
        },
        pendingSubRequest: true
      };
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        color: 'white',
        marginBottom: 2
      },
      pendingSubRequest: false
    };
  };
};


interface SFCCalendarProps {
  events: Event[];
  showFullEvents?: boolean;
  currentDates?: string[];
  filteredInstructorId?: number | null;
  linkParams?: string;
}

export const SFCCalendar: React.FC<SFCCalendarProps> = ({
    events,
    showFullEvents = true,
    currentDates,
    filteredInstructorId,
    linkParams
  }) => {
  return (
    <Calendar<Event>
      localizer={localizer}
      events={events}
      startAccessor="start"
      endAccessor="end"
      views={['month']} // Only show the month view
      defaultView="month" // Set the default view to month
      style={{ height: '100vh', background: '#FFFFFF' }}
      components={{
        toolbar: Toolbar,
        dateCellWrapper: (props) => {
          let dateString = props.value.toLocaleDateString();
          const eventList = events.filter(
            (event) =>
              event.start.toLocaleDateString() === dateString ||
              event.end.toLocaleDateString() === dateString
          );
          return (
              <CustomDayCellWrapper
                  {...props}
                  summarized={!showFullEvents}
                  events={eventList}
                  currentDates={currentDates}
                  linkParams={linkParams}
              />
          );
        },
        month: {
          header: (props) => <>{localizer.format(props.date, 'EE')}</>
        },
        eventWrapper: (props) => (
          <EventWrapper
            {...props}
            summarized={!showFullEvents}
            filteredInstructorId={filteredInstructorId}
          />
        ),
        event: (props) => (
          <EventCell
            {...props}
            filteredInstructorId={filteredInstructorId}
          />
        )
      }}
      eventPropGetter={createEventStyleGetterWithInstructor(filteredInstructorId)}
      showAllEvents
    />
  );
};
